import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, LayoutGridIcon } from 'lucide-react';
import { thingsboardService } from '../services/thingsboard';
import { useTelemetry } from '../hooks/useTelemetry';

interface CurtainControllerProps {
  isOpen: boolean;
  deviceName: string | null;
  alias?: string | null;
  onClose: () => void;
  sensors: any[];
}

export function CurtainController({ isOpen, deviceName, alias, onClose, sensors }: CurtainControllerProps) {
  const [percent, setPercent] = useState(100);
  const [hubSensor, setHubSensor] = useState<any>(null);

  useEffect(() => {
    const found = sensors.find(s => s.label === 'SEC_0');
    if (found) {
      setHubSensor(found);
    } else if (isOpen) {
      thingsboardService.getTenantDevice('SEC_0')
        .then(device => {
          if (device && device.id) {
            setHubSensor({ id: device.id.id, label: device.name, type: device.type });
          }
        })
        .catch(e => console.warn('Could not fetch SEC_0 by name', e));
    }
  }, [sensors, isOpen]);

  const targetSensor = useMemo(() => sensors.find(s => s.label === deviceName), [sensors, deviceName]);

  useEffect(() => {
    if (isOpen) {
      console.log('CurtainController Opened! ->', { 
        passedDeviceName: deviceName, 
        hubSensorFound: hubSensor, 
        targetSensorFound: targetSensor 
      });
    }
  }, [isOpen, deviceName, hubSensor, targetSensor]);

  // Use telemetry to get the real-time curtain_state
  const { data: realtimeData } = useTelemetry(targetSensor?.id || null, ['curtain_state']);

  useEffect(() => {
    if (isOpen && hubSensor && deviceName) {
      // Parse dev_no from deviceName. e.g., SEC_2 -> 2, SEC_01 -> 1
      const match = deviceName.match(/SEC_0?(\d+)/);
      if (match) {
        const devNo = parseInt(match[1], 10);
        console.log('Sending get_curtain_state command to hub:', hubSensor.id, 'with dev_no:', devNo);
        // Request the hub to fetch current state and update telemetry on the actual device
        thingsboardService.sendOneWayRpc(hubSensor.id, 'get_curtain_state', { value: 0, dev_no: devNo })
          .catch(err => console.error('Failed to request get_curtain_state', err));
      }
    }
  }, [isOpen, hubSensor, deviceName]);

  useEffect(() => {
    if (realtimeData && realtimeData['curtain_state']) {
      const stateArr = realtimeData['curtain_state'];
      if (stateArr.length > 0) {
        const val = Number(stateArr[0].value);
        if (!isNaN(val)) {
          setPercent(val);
        }
      }
    }
  }, [realtimeData]);

  const sendCommand = async (action: 'open' | 'close' | 'pause') => {
    if (!hubSensor || !deviceName) return;
    const match = deviceName.match(/SEC_0?(\d+)/);
    if (!match) return;
    const devNo = parseInt(match[1], 10);

    try {
      console.log('Sending set_curtain command to hub:', hubSensor.id, 'with dev_no:', devNo, 'and action:', action);
      await thingsboardService.sendOneWayRpc(hubSensor.id, 'set_curtain', { value: action, dev_no: devNo });
      // We can optimistically update or rely on telemetry
      if (action === 'open') setPercent(0);
      if (action === 'close') setPercent(100);

      // Trigger state reporting so the UI can verify the command execution
      /*setTimeout(() => {
        console.log('Requesting get_curtain_state after command');
        thingsboardService.sendOneWayRpc(hubSensor.id, 'get_curtain_state', { value: 0, dev_no: devNo })
          .catch(err => console.error('Failed to request get_curtain_state after command', err));
      }, 40000);*/
      
    } catch (err) {
      console.error(`Failed to send ${action} command via SEC_0`, err);
    }
  };

  const isBlind = deviceName?.includes('SEC_1') === false; // Let's say SEC_01 is curtain, rest are blinds
  const title = alias ? `${alias} - ${deviceName}` : deviceName;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[300] flex items-center justify-center"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 w-[90%] max-w-sm bg-slate-800/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
            <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
              <LayoutGridIcon className="w-5 h-5" />
            </div>
            <div className="text-base font-bold text-white">
              {title}
            </div>
          </div>

          <div className="px-5 pt-5 pb-3">
            <div className="relative w-full h-48 rounded-xl border border-white/10 bg-slate-900 overflow-hidden">
              <div className="absolute inset-2 rounded-lg border-2 border-slate-600 bg-gradient-to-b from-sky-900/30 to-sky-950/50 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-sky-800/20 to-transparent" />
                <motion.div
                  className="absolute top-0 left-0 right-0 bg-gradient-to-b from-amber-900/90 to-amber-950/90 overflow-hidden"
                  animate={{ height: `${percent}%` }}
                  transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                >
                  {isBlind && Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className="w-full h-[6.25%] border-b border-amber-700/50" />
                  ))}
                  <div className="absolute bottom-0 left-0 right-0 h-2 bg-amber-600/80 shadow-md" />
                </motion.div>
              </div>

              <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                <span className="text-xl font-black text-white font-roboto">{percent}</span>
                <span className="text-xs text-slate-400 ml-0.5">%</span>
              </div>
              <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm px-2.5 py-1 rounded-lg border border-white/10">
                <span className="text-[0.8rem] font-bold font-roboto text-amber-400 uppercase tracking-wider">
                  {percent >= 100 ? '닫힘' : percent <= 0 ? '열림' : '분할개방'}
                </span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="px-5 pb-3">
            <div className="flex gap-2">
              <button onClick={() => sendCommand('open')} className="flex-1 flex flex-col items-center gap-1 py-3 rounded-xl bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 active:scale-95 transition-all font-bold text-sm">
                <ChevronDownIcon className="w-5 h-5 rotate-180" />
                올림
              </button>
              <button onClick={() => sendCommand('pause')} className="flex-1 flex flex-col items-center gap-1 py-3 rounded-xl bg-slate-500/15 text-slate-300 border border-slate-500/20 hover:bg-slate-500/25 active:scale-95 transition-all font-bold text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <div className="w-3.5 h-3.5 rounded-sm bg-slate-400" />
                </div>
                정지
              </button>
              <button onClick={() => sendCommand('close')} className="flex-1 flex flex-col items-center gap-1 py-3 rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/20 hover:bg-amber-500/25 active:scale-95 transition-all font-bold text-sm">
                <ChevronDownIcon className="w-5 h-5" />
                내림
              </button>
            </div>
          </div>

          <div className="px-5 pb-4">
            <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-amber-500/20 text-amber-400 text-sm font-bold hover:bg-amber-500/30 transition-colors">
              닫기
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
